import type { ItemFieldRow } from './item-field-reader';
import { isOwnedRuleWindow } from './owned-rule-window';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { scoreCarrierIntegerLayerArithmetic } from './formula-variable-resolution';
import type { FormulaVariableMetadata } from './formula-variable-resolution';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { resolveCreatureSizeSourceBundle } from './creature-size-source-bundle';
import { readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { shiftCreatureSizeId, TINY_CREATURE_SIZE_ID } from './creature-size';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpAdd, phpFloatToInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const trim = (v: string): string => v.replace(/^[\x00\x09\x0a\x0b\x0d\x20]+|[\x00\x09\x0a\x0b\x0d\x20]+$/g, '');
function clone<T>(v: T): T {
    if (!arrayLike(v)) return v;
    const entries = Object.entries(v);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]))) as T;
}
export class CreatureSizeArrayWriteError extends Error {}
function writable(v: unknown): Row {
    if (v == null || v === false) return {};
    if (!arrayLike(v)) {
        if (typeof v === 'string') throw new TypeError('Cannot access offset of type string on string');
        throw new CreatureSizeArrayWriteError('Cannot use scalar as array');
    }
    return Object.fromEntries(Object.entries(v));
}
export interface CreatureSizeRecalculationDependencies extends FormulaReadDependencies {
    metadata: FormulaVariableMetadata;
    queryFullItem: (id: string) => ItemFieldRow | null;
    resolveItemName: (id: string) => unknown;
    /** Native blob/catalog lookup replies remain explicit at this boundary. */
    nameFromBlob: (row: ItemFieldRow, id: string) => string;
    resolveItemIdType: (id: string, row: ItemFieldRow) => string;
    /** immune_to_size_reduction (Wizard v9): the TeamType id of a status's caster (encounter_creature_id). Absent → the holder row's
     *  `encounter_arr.encounter_creatures_arr` roster is searched; an unknown team never counts as hostile (the shrink applies). */
    casterTeam?: (casterId: string) => unknown;
}
/** A team cell as stored: a bare TeamType UUID, or its [0] / {items:{item_id}} wrapper. '' when unknown. */
function teamId(value: unknown): string {
    let v = value;
    while (arrayLike(v)) v = own(v, '0') ?? own(own(v, 'items'), 'item_id') ?? null;
    return typeof v === 'string' && valid(v.trim()) ? v.trim().toUpperCase() : '';
}
/** Full recalc_creature_size source ordering and row writes, using the existing
 * pure list/scalar/status/source-bundle/formula readers. No event list is supplied.
 * A request owns its reader guards; definitions and provenance lookups are explicit.
 */
export function createCreatureSizeRecalculator(deps: CreatureSizeRecalculationDependencies): (input: ItemFieldRow) => ItemFieldRow {
    const readers = createFormulaReadContext(deps);
    const reference = (id: string): ItemFieldRow | null => { deps.trace?.(['reference', id]); return deps.resolveCarrierRef(id); };
    const name = (id: string): unknown => { deps.trace?.(['name', id]); return deps.resolveItemName(id); };
    const bundleDeps = { resolveCarrierRef: reference, resolveItemName: name,
        queryFullItem: (id: string) => { deps.trace?.(['full-item', id]); return deps.queryFullItem(id); },
        ...(deps.resolveInventoryIdentity ? { resolveInventoryIdentity: (id: string) => { deps.trace?.(['inventory-identity', id]); return deps.resolveInventoryIdentity!(id); } } : {}) };
    const current = (row: ItemFieldRow): string => {
        let chosen = '';
        for (const cell of Object.values(readers.layerEntries(row, 'size'))) { const id = readItemUuidSlot(cell); if (id !== '') chosen = id; }
        return chosen;
    };
    const has = (row: ItemFieldRow, key: string): boolean => readers.layerValue(row, key) !== null || phpJsonTruthy(readers.layerEntries(row, key));
    const score = (row: ItemFieldRow, plug: unknown): bigint => {
        const v = scoreCarrierIntegerLayerArithmetic(row, 'change_creature_size', plug, false, deps.metadata, readers);
        return typeof v === 'bigint' ? v : phpFloatToInteger(v);
    };
    return input => {
        if (!arrayLike(input)) throw new TypeError('Size recalculation requires a PHP array');
        const owned = isOwnedRuleWindow(input);
        const row = owned ? input as Row : writable(clone(input)), events: Row[] = [], fragments: { delta: bigint; source_id: string; source_label: unknown; source_kind: string; hostile?: boolean }[] = [];
        // immune_to_size_reduction (Wizard v9 "Stabilized Core"): the size-source bundles walked below, kept for the immunity read — done only
        // when a provably HOSTILE shrink exists, so an ordinary row reads nothing extra
        const providerArrs: { arr: ItemFieldRow; layer: string }[] = [];
        const holderId = phpJsonString(own(own(row, 'encounter_creatures'), 'encounter_creature_id') ?? '').trim().toUpperCase();
        // (stored cells only — encounter_row_team_uuid(): no layer read, so an ordinary recalculation's reads are unchanged)
        const holderTeam = (): string => teamId(own(own(row, 'encounter_creatures'), 'team') ?? own(row, 'team'));
        const casterTeamOf = (casterId: string): string => {
            if (deps.casterTeam) return teamId(deps.casterTeam(casterId));
            const roster = own(own(row, 'encounter_arr'), 'encounter_creatures_arr');
            for (const member of arrayLike(roster) ? Object.values(roster) : []) {
                const ec = own(member, 'encounter_creatures');
                if (phpJsonString(own(ec, 'encounter_creature_id') ?? '').trim().toUpperCase() === casterId) return teamId(own(ec, 'team') ?? own(member, 'team'));
            }
            return '';
        };
        // provably hostile: a caster other than the holder on the OPPOSING team — check_opposing_team(): Friend vs Enemy (a neutral or unknown
        // team never counts; fail toward the shrink). Same test as the browser apply gate (apply-status-effect.ts sizeReductionBlocked).
        const hostileStatus = (entry: unknown): boolean => {
            const raw = own(entry, 'status_caster_id') ?? own(entry, 'status_target_id');
            const caster = phpJsonString(arrayLike(raw) ? own(raw, '0') ?? '' : raw ?? '').trim().toUpperCase();
            if (caster === '' || caster === holderId || !valid(caster)) return false;
            const mine = holderTeam(), theirs = casterTeamOf(caster);
            const FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3', ENEMY = '9399856B-2132-7574-1684-91139A2831DD';
            return (mine === FRIEND && theirs === ENEMY) || (mine === ENEMY && theirs === FRIEND);
        };
        const writeSize = (value: unknown): void => { row.item_layer_types = writable(row.item_layer_types); (row.item_layer_types as Row).size = clone(value); };
        let base = current(row);
        if (base === '') { base = TINY_CREATURE_SIZE_ID; writeSize(base); }
        let ancestry = '';
        for (const key of ['ancestry_1', 'ancestry_2']) {
            const slot = own(own(row, 'player_characters'), key), cell = typeof slot === 'string' ? slot[0] : own(slot, '0');
            if (phpJsonTruthy(cell) && valid(phpJsonString(cell))) { ancestry = phpJsonString(cell); break; }
        }
        let directCurrent = base;
        const top = arrayLike(row.change_creature_size) ? row.change_creature_size : null;
        if (top !== null) {
            const raw = own(row.encounter_creatures, 'encounter_creature_monster_template_id'), id = typeof raw === 'string' && valid(raw) ? raw : '';
            const label = id !== '' ? deps.nameFromBlob(row, id) : '';
            for (const change of Object.values(top)) {
                const delta = score({ change_creature_size: [change] }, 0);
                if (delta !== 0n) fragments.push({ delta, source_id: id, source_label: label !== '' ? label : 'Monster template / direct layer', source_kind: 'monster_template' });
            }
            delete row.change_creature_size;
        }
        const buckets = [
            { layer: 'passive_ability', entries: row.passive_ability ?? [] },
            { layer: 'feat', entries: readers.layerEntries(row, 'feat') },
            { layer: 'status_effect', entries: readCreaturePayloadStatuses(row, readers.layerValue, reference) },
        ];
        for (const bucket of buckets) for (const entry of Object.values(readers.layerEntries(bucket, 'entries'))) {
            const bundle = resolveCreatureSizeSourceBundle(entry as ItemFieldRow | string, bucket.layer, bundleDeps);
            if (bundle === null) continue;
            const ability = bundle.item_arr, source = { source_id: bundle.source_id, source_label: bundle.source_label !== '' ? bundle.source_label : 'Unknown source', source_kind: bucket.layer };
            providerArrs.push({ arr: ability, layer: bucket.layer });
            if (readers.layerValue(ability, 'size') !== null) {
                const from = directCurrent; writeSize(readers.layerValue(ability, 'size'));
                const to = current(row) || from; directCurrent = to;
                if (from !== to) events.push({ kind: 'direct_set', from_size_id: from, to_size_id: to, ...source });
            }
            if (has(ability, 'change_creature_size')) for (const change of Object.values(readers.layerEntries(ability, 'change_creature_size'))) {
                const delta = score(row, change);
                if (delta !== 0n) fragments.push({ delta, ...source, ...(delta < 0n && bucket.layer === 'status_effect' && hostileStatus(entry) ? { hostile: true } : {}) });
            }
        }
        const seen = new Set<string>();
        for (const slot of ['creature_template', 'creature_sub_type', 'creature_type']) {
            const raws = [own(row.item_layer_types, slot), row[slot]].filter(v => v != null);
            for (const raw of raws) for (const entry of arrayLike(raw) && own(raw, '0') !== undefined ? Object.values(raw) : [raw]) {
                const id = trim(typeof entry === 'string' ? entry : arrayLike(entry) ? readLayerListEntryUuid(entry) || readItemUuidSlot(entry) : '');
                if (id === '' || !valid(id) || seen.has(id)) continue; seen.add(id);
                const carrier = arrayLike(entry) && (own(entry, 'items') != null || own(entry, 'item_layer_types') != null) ? entry : reference(id);
                if (!arrayLike(carrier) || !has(carrier, 'change_creature_size')) continue;
                const label = phpJsonString(readers.itemField(carrier, 'item_name') ?? ''), kind = phpJsonString(readers.itemField(carrier, 'item_type') ?? slot);
                for (const change of Object.values(readers.layerEntries(carrier, 'change_creature_size'))) {
                    const delta = score({ change_creature_size: [change] }, 0);
                    if (delta !== 0n) fragments.push({ delta, source_id: id, source_label: label !== '' ? label : 'Creature template', source_kind: kind !== '' ? kind : slot });
                }
            }
        }
        // immune_to_size_reduction: a hostile status's shrink is dropped when the holder root or any walked size-source bundle — its passives
        // (an aura / combat-start timed copy excepted: those reach it as status rows), feats, statuses — carries the flag. Read from the
        // AUTHORED keys only (own item_layer_types / root key), never through the readers: the reader trace of a row carrying none of the
        // three Wizard v9 layers stays exactly the PHP twin's (Codex review 2026-10-09, the size parity suite compares traces)
        if (fragments.some(frag => frag.hostile)) {
            const authored = (arr: unknown, name: string): unknown => { const v = own(own(arr, 'item_layer_types'), name) ?? own(arr, name); return arrayLike(v) ? own(v, '0') : v; };
            const deliveredAsStatus = (arr: ItemFieldRow): boolean => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(name => authored(arr, name) != null)
                || (authored(arr, 'triggers_after_combat_begins') != null && authored(arr, 'duration_type') != null);
            const immune = phpJsonTruthy(authored(row, 'immune_to_size_reduction'))
                || providerArrs.some(p => phpJsonTruthy(authored(p.arr, 'immune_to_size_reduction')) && !(p.layer === 'passive_ability' && deliveredAsStatus(p.arr)));
            if (immune) {
                const all = fragments.splice(0, fragments.length);
                for (const frag of all) {
                    if (!frag.hostile) { fragments.push(frag); continue; }
                    events.push({ kind: 'step_ignored', delta: Number(frag.delta), reason: 'immune_to_size_reduction', source_id: frag.source_id, source_label: frag.source_label, source_kind: frag.source_kind });
                }
            }
        }
        for (const frag of fragments) delete frag.hostile;
        const preBatch = current(row) || directCurrent;
        let total: PhpArithmetic = 0n;
        for (const frag of fragments) total = phpAdd(total, frag.delta);
        let running: PhpArithmetic = 0n;
        // Native typed ladder rejects out-of-range promoted totals. Never silently clamp them.
        const shift = (amount: PhpArithmetic): string => {
            if (typeof amount === 'number' && (!Number.isFinite(amount) || amount >= 9223372036854775808 || amount < -9223372036854775808)) throw new TypeError('Size delta exceeds native integer argument');
            return shiftCreatureSizeId(preBatch, Number(amount));
        };
        for (const frag of fragments) {
            const from = shift(running); running = phpAdd(running, frag.delta);
            events.push({ kind: 'step_change', delta: Number(frag.delta), from_size_id: from, to_size_id: shift(running), source_id: frag.source_id, source_label: frag.source_label, source_kind: frag.source_kind });
        }
        if (total !== 0n && total !== 0) { const size = shift(total); if (size !== '' && size !== current(row)) writeSize(size); }
        const final = current(row) || preBatch;
        if (top !== null) row.change_creature_size = top;
        for (const event of events) {
            const id = phpJsonString(event.source_id ?? '');
            if (id !== '' && valid(id) && deps.resolveItemIdType(id, row) === event.source_kind) delete event.source_kind;
        }
        const baseRow = ancestry !== '' && valid(ancestry) ? { size_id: base, source_id: ancestry }
            : { size_id: base, source_label: 'Base (ancestry / sub_type)', source_kind: 'ancestral' };
        row.computed = writable(row.computed); const computed = row.computed as Row;
        computed.creature = writable(computed.creature); const creature = computed.creature as Row;
        creature.size = writable(creature.size); (creature.size as Row).breakdown = { base: baseRow, final_size_id: final, ...(events.length ? { events } : {}) };
        return owned ? row : clone(row);
    };
}
